import { createContext, useContext, useEffect, useMemo, useCallback, useState, createElement } from 'react'
import { supabase } from '../lib/supabase.js'
import { _setApiToken, playerExists } from '../api/levelApi.js'
import { isPasswordRecovery, clearPasswordRecovery } from '../lib/recoveryFlag.js'
import { analytics } from '../lib/analytics.js'

const STORAGE_KEY = 'level_playerName'

// Beta cerrada por código de invitación: para email/password, Supabase ya
// distingue signUp() de signInWithPassword(), así que no hay hueco. Pero con
// Google/Apple, signInWithOAuth() es LA MISMA llamada tanto para iniciar
// sesión como para crear cuenta — entrar por "Iniciar sesión" (que no pide
// código, solo "Regístrate" lo pide) podía colarse como alta nueva sin
// invitación. Login.jsx marca INVITE_VALIDATED_KEY justo antes de lanzar el
// OAuth de registro (tras validar el código); si un SIGNED_IN llega SIN esa
// marca, se comprueba si la cuenta ya existía de antes — si es genuinamente
// nueva, se cierra la sesión inmediatamente en vez de dejar pasar.
export const INVITE_VALIDATED_KEY = 'nivela_invite_validated'
export const INVITE_BLOCKED_KEY = 'nivela_invite_blocked'

const PlayerContext = createContext(null)

function deriveDisplayName(user) {
  if (!user) return ''
  return (
    user.user_metadata?.display_name ||
    user.user_metadata?.full_name ||
    user.email?.split('@')[0] ||
    ''
  )
}

export function PlayerProvider({ children }) {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  // PASSWORD_RECOVERY es un evento de un solo disparo, emitido por supabase-js
  // al procesar el hash de la URL nada más arrancar el cliente — puede ocurrir
  // ANTES de que Login.jsx llegue a montar su propio listener (p.ej. si el
  // enlace aterriza en "/" y un salto interno de React Router lleva a
  // "/login"), con lo que ese listener local nunca lo recibe. Se expone aquí,
  // en el listener que sí está activo desde el arranque de la app, para que
  // Login.jsx lo lea de forma fiable sin depender de timing.
  // El valor inicial también se deriva del hash de la URL de forma síncrona
  // (no solo del evento, que llega async): si el navegador ya tiene un
  // nombre de jugador guardado de una sesión anterior (localStorage), el
  // efecto de onboarding en App.jsx puede evaluarse en el primer render,
  // antes de que el evento PASSWORD_RECOVERY llegue a marcar este estado —
  // mirar el hash ya en el primer render cierra esa ventana de carrera.
  const [passwordRecovery, setPasswordRecovery] = useState(() => isPasswordRecovery())

  // PlayerProvider envuelve toda la app y NUNCA se remonta durante su vida
  // (a diferencia de Login.jsx, que sí remonta en cada navegación a /login)
  // — así que el valor inicial de arriba solo es correcto la primera vez que
  // carga la página. Si la pestaña ya llevaba un rato abierta y con sesión
  // iniciada, y desde ahí se abre un enlace de recuperación NUEVO (sin
  // recargar del todo la página), ese valor inicial se queda anclado al
  // momento en que arrancó la app — nunca ve el hash nuevo. hashchange/
  // popstate SÍ se disparan aunque no haya recarga completa.
  useEffect(() => {
    function checkHash() {
      if (isPasswordRecovery()) setPasswordRecovery(true)
    }
    window.addEventListener('hashchange', checkHash)
    window.addEventListener('popstate', checkHash)
    return () => {
      window.removeEventListener('hashchange', checkHash)
      window.removeEventListener('popstate', checkHash)
    }
  }, [])

  // playerName: inicializado desde localStorage para evitar flash en recarga.
  // try/catch obligatorio aquí (a diferencia de otros usos más tolerantes):
  // esto corre en el primer render de PlayerProvider, el proveedor raíz de
  // toda la app — sin captura, un SecurityError de localStorage (navegación
  // privada estricta, restricción de sistema) tira la app entera abajo antes
  // de montar nada, con pantalla en blanco (visto en Sentry 2026-08-05, dos
  // alertas en /login — la ruta a la que caen '/' y '*' cuando la app no
  // llega a montar).
  const readStoredName = () => {
    try {
      return localStorage.getItem(STORAGE_KEY) || ''
    } catch {
      return ''
    }
  }
  const [playerName, setPlayerNameRaw] = useState(readStoredName)
  const [debouncedName, setDebouncedName] = useState(readStoredName)

  // Bootstrap: obtener sesión actual de Supabase al montar
  useEffect(() => {
    // Diagnóstico de sesiones perdidas (2026-10-08: la app de iOS pidió login
    // tras semanas sin abrirse). Si al arrancar había una sesión guardada y
    // aun así no se recupera, el fallo es del refresco del token; si no había
    // nada guardado, el dispositivo borró el almacenamiento. El evento solo
    // se manda en el primer caso — el segundo no se distingue de una visita nueva.
    let hadStoredSession = false
    try {
      hadStoredSession = Object.keys(localStorage).some(k => k.startsWith('sb-') && k.endsWith('-auth-token'))
    } catch { /* ignore */ }
    supabase.auth.getSession().then(({ data: { session: s }, error }) => {
      if (!s && hadStoredSession) {
        try {
          analytics.capture('session_restore_failed', {
            error: error?.message || null,
            error_code: error?.code || error?.status || null,
            native_ios: !!window.webkit?.messageHandlers?.requestDeviceToken,
          })
        } catch { /* ignore */ }
      }
      _setApiToken(s?.access_token ?? null)
      setSession(s)
      if (s?.user) {
        const name = deriveDisplayName(s.user)
        setPlayerNameRaw(name)
        try { localStorage.setItem(STORAGE_KEY, name) } catch { /* ignore */ }
      }
      setLoading(false)
    })

    // Un SIGNED_IN por OAuth (Google/Apple) sin la marca de código validado
    // puede ser una cuenta recién creada saltándose el código de invitación
    // (ver comentario junto a INVITE_VALIDATED_KEY) — se comprueba antes de
    // conceder acceso, no después, para no dejar ni un frame de acceso.
    async function admitSignedIn(s) {
      _setApiToken(s?.access_token ?? null)

      const validated = sessionStorage.getItem(INVITE_VALIDATED_KEY) === '1'
      if (validated) {
        sessionStorage.removeItem(INVITE_VALIDATED_KEY)
      } else {
        let exists = true
        try {
          exists = await playerExists()
        } catch {
          exists = true // fallo de red al comprobar: no bloquear a un usuario real por ello
        }
        if (!exists) {
          _setApiToken(null)
          try { sessionStorage.setItem(INVITE_BLOCKED_KEY, '1') } catch { /* ignore */ }
          await supabase.auth.signOut()
          return
        }
      }

      setSession(s)
      const name = deriveDisplayName(s.user)
      setPlayerNameRaw(name)
      try { localStorage.setItem(STORAGE_KEY, name) } catch { /* ignore */ }
    }

    // Escuchar cambios de auth (login, logout, refresh de token)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'SIGNED_IN' && s) {
        admitSignedIn(s)
        return
      }
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true)
      }
      _setApiToken(s?.access_token ?? null)
      setSession(s)
      if (s?.user) {
        const name = deriveDisplayName(s.user)
        setPlayerNameRaw(name)
        try { localStorage.setItem(STORAGE_KEY, name) } catch { /* ignore */ }
      } else {
        setPlayerNameRaw('')
        try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Debounce para slugificación (600ms igual que antes)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(playerName), 600)
    return () => clearTimeout(t)
  }, [playerName])

  // Sincronización entre pestañas (heredado)
  useEffect(() => {
    function onStorage(e) {
      if (e.key === STORAGE_KEY) {
        setPlayerNameRaw(e.newValue || '')
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // setPlayerName: actualiza nombre de display (no hace login)
  const setPlayerName = useCallback((name) => {
    setPlayerNameRaw(name)
    try { localStorage.setItem(STORAGE_KEY, name) } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('level-player-change', { detail: name }))
  }, [])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    clearPasswordRecovery()
  }, [])

  // Refuerzo del hashchange/popstate de arriba: App.jsx lo llama desde un
  // efecto atado a useLocation() (dentro del router, a diferencia de
  // PlayerProvider) — se dispara de forma fiable en cada cambio de ruta,
  // sea cual sea el mecanismo real detrás (recarga completa, pushState,
  // reutilización de pestaña), en vez de depender de que un evento concreto
  // del navegador llegue a saltar.
  const checkPasswordRecoveryHash = useCallback(() => {
    if (isPasswordRecovery()) setPasswordRecovery(true)
  }, [])

  const playerSlug = useMemo(() => {
    const base = debouncedName.trim()
    if (!base) return null
    return base.toLowerCase().replace(/\s+/g, '-')
  }, [debouncedName])

  const value = useMemo(
    () => ({
      // API pública original — sin cambios
      playerName,
      debouncedName,
      setPlayerName,
      playerSlug,
      // Nuevo
      session,
      user: session?.user ?? null,
      loading,
      signOut,
      accessToken: session?.access_token ?? null,
      passwordRecovery,
      checkPasswordRecoveryHash,
    }),
    [playerName, debouncedName, setPlayerName, playerSlug, session, loading, signOut, passwordRecovery, checkPasswordRecoveryHash]
  )

  return createElement(PlayerContext.Provider, { value }, children)
}

export function usePlayer() {
  const ctx = useContext(PlayerContext)
  if (!ctx) {
    throw new Error('usePlayer must be used within PlayerProvider')
  }
  return ctx
}
